/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */
import TencentCloudChat from '@tencentcloud/chat';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Dialog, Loading, MessagePlugin, Space, Switch } from 'tdesign-react';

import { CardFormComponent, IFormItemProps } from '@/components';
import TipDialog, { ITipDialogRef } from '@/components/tipDialog';
import { FormOption, getSaveTip, SAVE_TO_DRAFT, SAVE_TO_PUBLISH } from '@/const/form';
import { AuditStatusType } from '@/const/status';
import { PageIds } from '@/const/tip';
import { eventPool } from '@/event/eventPool';
import { useEventBus } from '@/hooks/useEventBus';
import { BotMode, IBot, IFormResult } from '@/interface';
import { PreviewSkeleton } from '@/pages/preview';
import { deleteBotMemory, modifyBotDetail, submitBot } from '@/services/bot';
import { useAppDispatch, useTypedSelector } from '@/store';
import { clearBotInfo, createNewBot, getBotDetail, setDisabled, updateBotInfo } from '@/store/modules/bot';
import { setStoryErrors } from '@/store/modules/story';
import { isWoa } from '@/utils/getApiDomain';
import { getFormErrorMsg } from '@/utils/getFormErrors';
import { getMaterial } from '@/utils/material';
import { orgConfigs } from '@/utils/orgFormConfig';

import BotGenerator from './botGenerator';
import BotStatus from './botStatus';
import { botFormCard1, botFormCard2, botFormCard3 } from './form-config';
import styles from './index.module.less';

interface IBotInfoProps {
  visible: boolean;
  onClose: () => void;
  onConfirm?: (value: Partial<IBot>) => void;
  id?: string;
}

const BotInfo: React.FC<IBotInfoProps> = (props) => {
  const { id, visible, onClose, onConfirm } = props;
  const dispatch = useAppDispatch();
  const { chat } = useTypedSelector(store => store.chat);
  const { vuid } = useTypedSelector(store => store.user);
  const { woaUser } = useTypedSelector(store => store.auth);
  const { disabled, loading, botInfo } = useTypedSelector(store => store.bot);

  const [vis, setVis] = useState(false);
  const [statusVis, setStatusVis] = useState(false);

  useEffect(() => {
    if (visible) {
      setVis(visible);
      const timer = setTimeout(() => {
        setStatusVis(true);
        clearTimeout(timer);
      }, 400);
    }
  }, [visible]);

  const cardFormRef1 = useRef<any>(null);
  const cardFormRef2 = useRef<any>(null);
  const cardFormRef3 = useRef<any>(null);
  const containerRef = useRef<any>();
  const tipRef = useRef<ITipDialogRef>(null);

  const [searchParams] = useSearchParams();
  const release = searchParams.get('release') === 'true';

  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [mode, setMode] = useState<BotMode>(BotMode.BOT_MODE_DEFAULT);

  const [materialMap, setMaterialMap] = useState<Record<string, string>>(botInfo?.materialMap || {});
  useEffect(() => {
    setMaterialMap(botInfo?.materialMap || {});
  }, [botInfo?.materialMap]);

  useEventBus(eventPool.eventsTypes.MATERIAL_CHANGE, (payload: any) => {
    const result = getMaterial(payload, { ...materialMap });
    setMaterialMap(result);
  });

  useEffect(() => {
    if (id) {
      dispatch(getBotDetail({ id, release }));
    } else {
      dispatch(createNewBot());
    }
  }, [id, release]);

  const deleteFriend = useCallback(async () => {
    if (chat && id) {
      try {
        dispatch(setDisabled(true));
        const { code, message, msg } = await deleteBotMemory({
          fromUserId: isWoa ? woaUser || '' : `cms_${vuid}`,
          toUserId: id,
        });
        if (code !== 0) {
          MessagePlugin.error(message || msg || '后台服务错误');
          return;
        }
        const res = await chat.getConversationProfile(`C2C@RBT#${id}`);
        if (res?.data?.conversation) {
          await chat.clearHistoryMessage(`C2C@RBT#${id}`);
        }
        chat.deleteFriend({
          userIDList: [`@RBT#${id}`],
          type: TencentCloudChat.TYPES.SNS_DELETE_TYPE_BOTH,
        });
      } catch { }
    }
  }, [chat, id, vuid, woaUser, isWoa]);

  const handleClose = useCallback(() => {
    setVis(false);
    dispatch(clearBotInfo());
    setMode(BotMode.BOT_MODE_DEFAULT);
    cardFormRef1.current?.formRef?.form.reset();
    cardFormRef2.current?.formRef?.form.reset();
    cardFormRef3.current?.formRef?.form.reset();
    cardFormRef1.current?.formRef?.form.setFieldsValue({
      name: '未命名',
    });
    const timer = setTimeout(() => {
      onClose();
      clearTimeout(timer);
    }, 200);
  }, [onClose]);

  const handleSubmit = useCallback(async (saveType?: FormOption) => {
    if (isEmpty(botInfo)) {
      return;
    }
    setSaveLoading(true);
    const postData = {
      ...botInfo, materialMap,
    };
    const tip = getSaveTip(saveType);
    const { code, message, data } = await modifyBotDetail(postData);
    if (code !== 0) {
      MessagePlugin.error({ content: message || tip.error });
      setSaveLoading(false);
      return;
    }
    if (saveType !== FormOption.AUTO_SAVE) {
      onConfirm?.({ ...botInfo, id: data.id, createdBy: `${vuid}` });
    }
    if (saveType === FormOption.PUBLISH && (botInfo?.id || data.id)) {
      const { code, msg, message } = await submitBot(botInfo?.id || data.id);
      if (code !== 0) {
        MessagePlugin.error({ content: `保存成功，${msg || message || tip.error}` });
        await deleteFriend();
        setSaveLoading(false);
        handleClose();
        return;
      }
    }
    await deleteFriend();
    setSaveLoading(false);
    MessagePlugin.success({
      content: tip.success,
      placement: 'top',
    }).then(() => {
      handleClose();
    });
  }, [botInfo, handleClose, deleteFriend, materialMap, onConfirm, vuid]);

  const saveBot = useCallback((option: {
    type: FormOption,
    value: boolean
  }) => {
    if (option.type === FormOption.PUBLISH) {
      Promise.all([
        cardFormRef1.current?.formRef?.form.validate(),
        cardFormRef2.current?.formRef?.form.validate(),
        cardFormRef3.current?.formRef?.form.validate()]).then((res) => {
        if (res.every(item => item === true)) {
          handleSubmit(option.type);
        }
      });
    }
    if (option.type === FormOption.DRAFT) {
      handleSubmit(option.type);
    }
  }, [handleSubmit]);

  const handleGenerate = useCallback((value: Partial<IBot>) => {
    dispatch(updateBotInfo(value));
    cardFormRef1.current.formRef.form.setFieldsValue({ ...value });
    cardFormRef2.current.formRef.form.setFieldsValue({ ...value });
    cardFormRef3.current.formRef.form.setFieldsValue({ ...value });
  }, []);

  const onValuesChange = useCallback((content: any) => {
    if ('customBotCreateBaseInfo' in content) {
      dispatch(updateBotInfo({ ...content.customBotCreateBaseInfo }));
    } else {
      dispatch(updateBotInfo({ ...(content || {}) }));
    }
  }, []);

  const handleChangeMode = useCallback(() => {
    setMode(pre => (pre === BotMode.BOT_MODE_DEFAULT ? BotMode.BOT_MODE_IP : BotMode.BOT_MODE_DEFAULT));
  }, []);

  const handleGetErrorMsg = (result: IFormResult['result']) => {
    const errors = getFormErrorMsg(result);
    setErrorMsg(errors);
    dispatch(setStoryErrors({ payload: errors }));
    if (errors) {
      tipRef.current?.open();
    }
  };

  const refactorFieldsConfig = useMemo(() => (
    config: IFormItemProps[],
    values: Partial<IBot>,
  ) => orgConfigs(
    config,
    values, {
      promptTextKey: 'background',
      disabled: release || botInfo?.status === AuditStatusType.STATUS_TYPE_MANUAL_ING,
    },
  ), [release, botInfo?.status]);

  const handleClick1 = useCallback(() => {
    saveBot(SAVE_TO_DRAFT);
  }, [saveBot]);

  const handleClick2 = useCallback(() => {
    saveBot(SAVE_TO_PUBLISH);
  }, [saveBot]);

  if (id !== undefined && botInfo?.id === undefined) {
    return <div></div>;
  }

  return (
    <>
      <Dialog
        className={styles.dialog}
        visible={vis}
        width={'calc(100vw - 60px)'}
        style={{ background: '#2B2B2B', padding: 0, minWidth: 900 }}
        closeBtn={null}
        destroyOnClose={true}
        placement='top'
        header={
          <div className={styles.header}>
            {statusVis && botInfo && <BotStatus
              id={botInfo.id}
              status={botInfo.status || AuditStatusType.STATUS_TYPE_DEFAULT} />}
            <div className={styles.left}>
              <div className={styles.text}>
                {id === undefined ? '创建角色' : botInfo?.name}
              </div>
              {id === undefined
                && <BotGenerator
                  onComplete={handleGenerate}
                />}
            </div>
          </div>}
        footer={<div className={styles.footer}>
          <Button
            className='nova-info-button'
            size="medium"
            block
            theme="default"
            disabled={saveLoading || loading}
            onClick={handleClose}>
            取消
          </Button>
          <Button
            className='nova-info-button'
            size="medium"
            block
            disabled={disabled || loading || saveLoading || (release && botInfo?.id !== undefined) || botInfo?.status === AuditStatusType.STATUS_TYPE_MANUAL_ING}
            theme="default"
            onClick={handleClick1}>
            保存
          </Button>
          <Button className='nova-info-button' size="medium" block
            disabled={disabled || loading || saveLoading || (release && botInfo?.id !== undefined) || botInfo?.status === AuditStatusType.STATUS_TYPE_MANUAL_ING}
            theme="primary" onClick={handleClick2}>
            发布
          </Button>
        </div>}
      >
        <div className={styles.container} ref={containerRef}>
          <Loading size="small" loading={!!id && !botInfo?.id} showOverlay>
            <TipDialog tipRef={tipRef} message={errorMsg} />
            <Space className={styles.left} direction="vertical" size="large">
              <div className={styles['form-wrapper']}>
                <CardFormComponent
                  cRef={cardFormRef1}
                  key={botFormCard1.key}
                  title={botFormCard1.title}
                  pageId={PageIds.BOT_INFO_1}
                  className={styles.form}
                  formData={{
                    ...botInfo,
                    customBotCreateBaseInfo: {
                      avatar: botInfo?.avatar,
                      addUserForeBackground: botInfo?.addUserForeBackground,
                      chatBackground: botInfo?.chatBackground,
                    },
                  }}
                  hasCollapse
                  fieldsConfig={botFormCard1.fieldsConfig}
                  formConfig={{
                    layout: 'vertical',
                    labelWidth: '100%',
                    labelAlign: 'top',
                    onValuesChange: (content: any) => onValuesChange(content),
                  }}
                  onSubmit={handleSubmit}
                  extendControlConfig={{
                    refactorFieldsConfig: config => refactorFieldsConfig(config, botInfo || {}),
                    onSubmitFail: (result: IFormResult['result']) => {
                      handleGetErrorMsg(result);
                    },
                  }}
                ></CardFormComponent>
              </div>
              <div className={styles['form-wrapper']} style={{
                display: mode === BotMode.BOT_MODE_IP ? 'block' : 'none',
              }}>
                <Switch
                  className={styles.switch}
                  label={'高级模式'}
                  value={mode !== BotMode.BOT_MODE_DEFAULT}
                  onChange={handleChangeMode} />
                <CardFormComponent
                  cRef={cardFormRef2}
                  key={botFormCard2.key}
                  title={botFormCard2.title}
                  pageId={PageIds.BOT_INFO_2}
                  formData={{ ...botInfo }}
                  hasCollapse
                  fieldsConfig={botFormCard2.fieldsConfig}
                  formConfig={{
                    layout: 'vertical',
                    labelWidth: '100%',
                    labelAlign: 'top',
                    onValuesChange: (content: any) => onValuesChange(content),
                  }}
                  onSubmit={handleSubmit}
                  extendControlConfig={{
                    refactorFieldsConfig: config => refactorFieldsConfig(config, botInfo || {}),
                    onSubmitFail: (result: IFormResult['result']) => {
                      handleGetErrorMsg(result);
                    },
                  }}
                ></CardFormComponent>
              </div>
              <div className={styles['form-wrapper']} style={{
                display: mode === BotMode.BOT_MODE_DEFAULT ? 'block' : 'none',
              }}>
                {isWoa
                  && <Switch
                    className={styles.switch}
                    label={'高级模式'}
                    value={mode !== BotMode.BOT_MODE_DEFAULT}
                    onChange={handleChangeMode} />
                }
                <CardFormComponent
                  key={botFormCard3.key}
                  cRef={cardFormRef3}
                  title={botFormCard3.title}
                  pageId={PageIds.BOT_INFO_3}
                  formData={{ ...botInfo }}
                  hasCollapse
                  fieldsConfig={botFormCard3.fieldsConfig}
                  formConfig={{
                    layout: 'vertical',
                    labelWidth: '100%',
                    labelAlign: 'top',
                    onValuesChange: (content: any) => onValuesChange(content),
                  }}
                  onSubmit={handleSubmit}
                  extendControlConfig={{
                    refactorFieldsConfig: config => refactorFieldsConfig(config, botInfo || {}),
                    onSubmitFail: (result: IFormResult['result']) => {
                      handleGetErrorMsg(result);
                    },
                  }}
                ></CardFormComponent>
              </div>
            </Space>
          </Loading>
          <PreviewSkeleton pageKey='bot' mode='drawer' node={containerRef.current} />
        </div >
      </Dialog>
    </>
  );
};

export default BotInfo;
